import { useI18n } from "../../i18n/LanguageProvider";
import { Container, SectionLabel } from "../Container";
import { Reveal } from "../Reveal";
import { CollectionsPanel, InvoiceSnippet, PayLinkSnippet } from "../AppUI";
import { Gauge, Zap, FileText, CreditCard, Users, ShieldCheck, Wallet, History } from "lucide-react";

const icons = [Gauge, Zap, FileText, CreditCard, Users, ShieldCheck, Wallet, History];

function IconCard({ card, Icon }) {
  return (
    <div className="group h-full rounded-3xl border border-black/[0.07] bg-white p-8 transition-[transform,background-color,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-black hover:bg-black hover:shadow-[0_24px_60px_-24px_rgba(8,172,125,0.5)]">
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-black/[0.05] transition-colors duration-300 group-hover:[background-image:linear-gradient(135deg,#08AC7D_0%,#4DD79A_100%)]">
        <Icon strokeWidth={1.75} className="h-5 w-5 text-black" />
      </div>
      <h3 className="mt-6 text-lg font-semibold tracking-tight text-black transition-colors duration-300 group-hover:text-white">{card.title}</h3>
      <p className="mt-2 text-base leading-relaxed text-black/55 transition-colors duration-300 group-hover:text-white/60">{card.text}</p>
    </div>
  );
}

export function Features() {
  const { t } = useI18n();
  const cards = t("features.cards");
  const c = (i) => cards[i] || { title: "", text: "" };

  return (
    <section id="features" className="scroll-mt-24 bg-white py-28 md:py-36">
      <Container>
        <div className="max-w-3xl">
          <SectionLabel>{t("features.label")}</SectionLabel>
          <Reveal>
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-black sm:text-4xl lg:text-5xl">
              {t("features.title")}
            </h2>
          </Reveal>
        </div>

        {/* Top bento: big UI panel + two UI snippet cards */}
        <div className="mt-16 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <div className="relative flex h-full flex-col overflow-hidden rounded-3xl border border-green/15 bg-[#EAF7F0] p-8 md:p-10">
              <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-green/15 blur-3xl" />
              <div className="relative max-w-xs">
                <h3 className="text-2xl font-semibold tracking-tight text-black">{c(0).title}</h3>
                <p className="mt-3 text-base leading-relaxed text-black/60">{c(0).text}</p>
              </div>
              <div className="relative mt-8 flex justify-center lg:justify-start">
                <CollectionsPanel className="max-w-[340px]" />
              </div>
            </div>
          </Reveal>

          <div className="flex flex-col gap-4">
            <Reveal delay={0.08}>
              <div className="rounded-3xl border border-black/[0.07] bg-white p-6">
                <h3 className="text-lg font-semibold tracking-tight text-black">{c(2).title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-black/55">{c(2).text}</p>
                <div className="mt-5"><InvoiceSnippet /></div>
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="rounded-3xl border border-black/[0.07] bg-white p-6">
                <h3 className="text-lg font-semibold tracking-tight text-black">{c(6).title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-black/55">{c(6).text}</p>
                <div className="mt-5"><PayLinkSnippet /></div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Bottom row: icon cards with varied treatment */}
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 3, 4, 7].map((i, k) => (
            <Reveal key={i} delay={k * 0.05}>
              <IconCard card={c(i)} Icon={icons[i]} />
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
